import React, { useEffect, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { dashboardAPI } from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { ChartBarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const SalesGraph = () => {
  const { theme } = useTheme();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState<
    'today' | 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year'
  >('sixMonths');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getSalesGraph(currentTime);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentTime]);

  const isDark = theme === 'dark';

  const options = {
    animationEnabled: true,
    theme: isDark ? 'dark2' : 'light2',
    backgroundColor: 'transparent',
    title: {
      fontSize: 0,
    },
    axisX: {
      valueFormatString: 'DD MMM',
      labelAngle: -45,
      intervalType: 'minute',
      gridColor: isDark ? '#374151' : '#f3f4f6',
      lineColor: isDark ? '#4b5563' : '#e5e7eb',
      tickColor: isDark ? '#4b5563' : '#e5e7eb',
      labelFontFamily: 'Montserrat, sans-serif',
      labelFontSize: 12,
      labelFontColor: isDark ? '#9ca3af' : '#6b7280',
    },
    axisY: {
      prefix: 'PKR ',
      gridColor: isDark ? '#374151' : '#f3f4f6',
      lineColor: isDark ? '#4b5563' : '#e5e7eb',
      tickColor: isDark ? '#4b5563' : '#e5e7eb',
      labelFontFamily: 'Montserrat, sans-serif',
      labelFontSize: 12,
      labelFontColor: isDark ? '#9ca3af' : '#6b7280',
    },
    toolTip: {
      shared: true,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      fontColor: isDark ? '#ffffff' : '#1f2937',
      borderThickness: 0,
      cornerRadius: 8,
      fontFamily: 'Montserrat, sans-serif',
      contentFormatter: function (e: any) {
        return `📅 ${e.entries[0].dataPoint.x.toLocaleString()} <br/><strong>PKR ${e.entries[0].dataPoint.y.toFixed(
          2
        )}</strong>`;
      },
    },
    data: [
      {
        yValueFormatString: 'PKR #,###',
        xValueFormatString: 'DD MMM HH:mm',
        type: 'area',
        fillOpacity: 0.3,
        markerSize: 6,
        markerType: 'circle',
        lineColor: '#4B8845',
        color: '#6BA864',
        lineThickness: 3,
        dataPoints: stats
          ?.map((stat: any) => ({
            x: new Date(stat.date),
            y: stat.total,
          }))
          .sort((a: any, b: any) => a.x - b.x),
      },
    ],
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with gradient accent */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-md">
            <ChartBarIcon className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Sales Overview
          </h2>
        </div>

        {/* Custom styled select */}
        <div className="relative">
          <select
            className="appearance-none pl-4 pr-10 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:border-primary-300 transition-colors cursor-pointer"
            value={currentTime}
            onChange={(e) => setCurrentTime(e.target.value as any)}
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="threeMonths">Last 3 Months</option>
            <option value="sixMonths">Last 6 Months</option>
            <option value="year">This Year</option>
          </select>
          <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {/* Chart Container */}
      <div className="flex-1 min-h-[300px]">
        {!loading ? (
          <CanvasJSChart options={options} />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-600 mb-3"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                Loading sales data...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesGraph;
